import { z } from 'zod';

export enum RevRecTriggerEnum {
  INVOICE_CREATED = 'INVOICE_CREATED',
  INVOICE_PAID = 'INVOICE_PAID',
}

export const RevRecTriggerEnumZ = z.enum(RevRecTriggerEnum);

export const REV_REC_TRIGGER_DISPLAY: {
  [key in RevRecTriggerEnum]: string;
} = {
  INVOICE_CREATED: 'Invoice created',
  INVOICE_PAID: 'Invoice Paid',
};

export enum RevRecProrationModelEnum {
  DAY_BASED = 'DAY_BASED',
  MONTH_BASED = 'MONTH_BASED',
}

export const RevRecProrationModelEnumZ = z.enum(RevRecProrationModelEnum);

export const REV_REC_PRORATION_MODEL_DISPLAY: {
  [key in RevRecProrationModelEnum]: string;
} = {
  DAY_BASED: 'Day Based',
  MONTH_BASED: 'Month Based',
};

export const RevRecSettingsResponse = z.object({
  enabled: z.boolean(),
  prorationModel: RevRecProrationModelEnumZ,
});

export type IRevRecSettingsResponse = z.infer<typeof RevRecSettingsResponse>;

export const RevRecWaterfallRequest = z.object({
  startPeriod: z.string(),
  endPeriod: z.string(),
  recognizedAsOfPeriod: z.string(),
  currency: z.string().default('USD'),
});

export type IRevRecWaterfallRequest = z.infer<typeof RevRecWaterfallRequest>;

export const RevenueRecognitionPeriod = z.object({
  period: z.string(),
  recognizedAmount: z.number(),
  deferredAmount: z.number(),
});
export type IRevenueRecognitionPeriod = z.infer<
  typeof RevenueRecognitionPeriod
>;

export const RevenueRecognitionSchedule = z.object({
  bookingPeriod: z.string(),
  totalAmount: z.number(),
  recognizedAsOf: RevenueRecognitionPeriod,
  periods: RevenueRecognitionPeriod.array(),
});
export type IRevenueRecognitionSchedule = z.infer<
  typeof RevenueRecognitionSchedule
>;

export const RevenueRecognitionTotals = z.object({
  amount: z.number(),
  recognizedAmount: z.number(),
  deferredAmount: z.number(),
  periods: RevenueRecognitionPeriod.array(),
});
export type IRevenueRecognitionTotals = z.infer<
  typeof RevenueRecognitionTotals
>;

export const RevenueRecognitionResponse = z.object({
  startPeriod: z.string(),
  endPeriod: z.string(),
  recognizedAsOfPeriod: z.string(),
  currency: z.string(),
  recognitionSchedule: RevenueRecognitionSchedule.array(),
  totals: RevenueRecognitionTotals,
});
export type IRevenueRecognitionResponse = z.infer<
  typeof RevenueRecognitionResponse
>;

export interface RevRecV2RequestPayload {
  type: 'BY_YEAR' | 'BY_MONTH' | 'ALL_TIME';
  year?: number;
  month?: number;
}

export interface IRevenueScheduleSearchParams {
  id?: string;
  version?: number;
  contractId?: string;
  quoteId?: string;
  accountId?: string;
  revenueContractId?: string;
}

export enum RevenueScheduleStatusEnum {
  ACTIVE = 'ACTIVE',
}

export const RevenueScheduleStatusEnumZ = z.enum(RevenueScheduleStatusEnum);

export const RevenueScheduleTerm = z.object({
  start: z.string(),
  endInclusive: z.string(),
});
export type IRevenueScheduleTerm = z.infer<typeof RevenueScheduleTerm>;

export const RevenueScheduleContractData = z.object({
  term: RevenueScheduleTerm,
  amount: z.number(),
});
export type IRevenueScheduleContractData = z.infer<
  typeof RevenueScheduleContractData
>;

export const RevenueScheduleReferences = z.object({
  tenantId: z.string().optional(),
  contractId: z.string().optional(),
  accountId: z.string().optional(),
  quoteId: z.string().optional(),
  revenueContractId: z.string().optional(),
});
export type IRevenueScheduleReferences = z.infer<
  typeof RevenueScheduleReferences
>;

export const RevenueScheduleSummary = z.object({
  id: z.string(),
  version: z.number().optional(),
  status: z.string().optional(),
  contractData: RevenueScheduleContractData.optional(),
  references: RevenueScheduleReferences.optional(),
});
export type IRevenueScheduleSummary = z.infer<typeof RevenueScheduleSummary>;

export enum RevRecMethodEnum {
  IMMEDIATE = 'IMMEDIATE',
  EVENLY_MONTHLY_IN_ADVANCE = 'EVENLY_MONTHLY_IN_ADVANCE',
  EVENLY_MONTHLY_IN_ARREARS = 'EVENLY_MONTHLY_IN_ARREARS',
  STRAIGHT_LINE_EXACT_DAYS = 'STRAIGHT_LINE_EXACT_DAYS',
  STRAIGHT_LINE_EVEN_PERIODS = 'STRAIGHT_LINE_EVEN_PERIODS',
  MANUAL_OVERRIDE = 'MANUAL_OVERRIDE',
}

export const REV_REC_METHOD_DISPLAY: Record<string, string> = {
  [RevRecMethodEnum.IMMEDIATE]: 'Immediate',
  [RevRecMethodEnum.EVENLY_MONTHLY_IN_ADVANCE]: 'Evenly Monthly (Advance)',
  [RevRecMethodEnum.EVENLY_MONTHLY_IN_ARREARS]: 'Evenly Monthly (Arrears)',
  [RevRecMethodEnum.STRAIGHT_LINE_EXACT_DAYS]: 'Straight-line (Exact Days)',
  [RevRecMethodEnum.STRAIGHT_LINE_EVEN_PERIODS]: 'Straight-line (Even Periods)',
  [RevRecMethodEnum.MANUAL_OVERRIDE]: 'Custom',
};

export const RevenueSchedulePeriodRange = z.object({
  start: z.string(),
  endInclusive: z.string(),
});
export type IRevenueSchedulePeriodRange = z.infer<
  typeof RevenueSchedulePeriodRange
>;

export enum RevenueScheduleSourceTypeEnum {
  USER = 'USER',
  QUOTE = 'QUOTE',
}

export const RevenueScheduleSourceTypeEnumZ = z.enum(
  RevenueScheduleSourceTypeEnum,
);

export const REVENUE_SCHEDULE_SOURCE_TYPE_DISPLAY: Record<
  RevenueScheduleSourceTypeEnum,
  string
> = {
  [RevenueScheduleSourceTypeEnum.USER]: 'User',
  [RevenueScheduleSourceTypeEnum.QUOTE]: 'Quote',
};

export const RevenueSchedulePeriodReferences = z.object({
  subscriptionId: z.string().optional(),
  subscriptionItemId: z.string().optional(),
  offeringId: z.string().optional(),
  productId: z.string().optional(),
  sourceId: z
    .object({
      type: RevenueScheduleSourceTypeEnumZ.optional(),
      value: z.string().optional(),
    })
    .optional(),
});
export type IRevenueSchedulePeriodReferences = z.infer<
  typeof RevenueSchedulePeriodReferences
>;

export const RevenueSchedulePeriodMetadata = z.object({
  createDate: z.string().optional(),
  createdBy: z.string().optional(),
  lastModifiedDate: z.string().optional(),
  lastModifiedBy: z.string().optional(),
});
export type IRevenueSchedulePeriodMetadata = z.infer<
  typeof RevenueSchedulePeriodMetadata
>;

export const RevenueScheduleSubscriptionItemReferences = z.object({
  revenueScheduleId: z.string().optional(),
  subscriptionId: z.string().optional(),
  productId: z.string().optional(),
  offeringId: z.string().optional(),
});
export type IRevenueScheduleSubscriptionItemReferences = z.infer<
  typeof RevenueScheduleSubscriptionItemReferences
>;

export const RevenueScheduleSubscriptionItem = z.object({
  subscriptionItemId: z.string(),
  references: RevenueScheduleSubscriptionItemReferences,
  productName: z.string().optional(),
  productAmount: z.number().optional(),
  productType: z.string().optional(),
  recognitionMethod: z.string().optional(),
  recognitionDuration: z.number().nullish(),
  offeringName: z.string().optional(),
});
export type IRevenueScheduleSubscriptionItem = z.infer<
  typeof RevenueScheduleSubscriptionItem
>;

export interface IRevenueScheduleSubscriptionItemSearchParams {
  revenueScheduleId?: string;
  subscriptionId?: string;
  subscriptionItemId?: string;
  productId?: string;
  offeringId?: string;
}

export const AccountingPeriodLock = z.object({
  id: z.string(),
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  status: z.string(),
  lockedBy: z.string().optional(),
  persistenceMetadata: RevenueSchedulePeriodMetadata.optional(),
});
export type IAccountingPeriodLock = z.infer<typeof AccountingPeriodLock>;

export const RevenueSchedulePeriod = z.object({
  id: z.string(),
  revenueScheduleId: z.string(),
  period: RevenueSchedulePeriodRange,
  revenue: z.number(),
  cumulativeRevenue: z.number().optional(),
  deferredRevenue: z.number().optional(),
  productName: z.string().optional(),
  productAmount: z.number(),
  productType: z.string().optional(),
  recognitionMethod: z.string().optional(),
  recognitionDuration: z.number().nullish(),
  offeringName: z.string().optional(),
  references: RevenueSchedulePeriodReferences,
  persistenceMetadata: RevenueSchedulePeriodMetadata.optional(),
});
export type IRevenueSchedulePeriod = z.infer<typeof RevenueSchedulePeriod>;

export const CreateAccountingPeriodLockRequest = z.object({
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
});
export type ICreateAccountingPeriodLockRequest = z.infer<
  typeof CreateAccountingPeriodLockRequest
>;

export interface IRevenueSchedulePeriodSearchParams {
  windowStart?: string;
  windowEnd?: string;
  subscriptionId?: string;
  subscriptionItemId?: string;
  offeringId?: string;
  productId?: string;
  sourceId?: string;
}
